import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { isSfsLockedLoanUser, User } from '../models/auth.model';
import { AuthService } from '../services/auth.service';
import { DealerStateService } from '../services/dealer-state.service';

const FAQ_PATH = '/rehash-sfs/faq';

/**
 * Restricts navigation for JWT SFS users with a locked loan (`isSfs` + `sfs_loan_id`).
 * Allowed routes: `/rehash-sfs/{sfsLoanId}` and `/rehash-sfs/faq`.
 * All other routes redirect to the locked loan detail page.
 */
@Injectable({
  providedIn: 'root'
})
export class SfsLoanLockGuard {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.checkLockedRoute(state.url);
  }

  canActivateChild(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.checkLockedRoute(state.url);
  }

  checkLockedRoute(url: string): boolean | UrlTree {
    const user = this.authService.getUser();
    if (!isSfsLockedLoanUser(user)) {
      return true;
    }

    const normalizedUrl = this.normalizeUrl(url);
    const loanPath = `/rehash-sfs/${user.sfsLoanId}`;

    if (normalizedUrl === FAQ_PATH || normalizedUrl === loanPath) {
      return true;
    }

    return this.router.createUrlTree(['/rehash-sfs', user.sfsLoanId]);
  }

  private normalizeUrl(url: string): string {
    const pathOnly = url.split('?')[0].split('#')[0];
    if (pathOnly.length > 1 && pathOnly.endsWith('/')) {
      return pathOnly.slice(0, -1);
    }
    return pathOnly;
  }
}

/**
 * Resolves the default home redirect target for the given user.
 */
export function resolveDefaultHomeRedirect(
  user: User | undefined,
  router: Router,
  isStePricingGroup = false
): string | UrlTree {
  if (user?.isSfs && user.sfsLoanId) {
    return router.createUrlTree(['/rehash-sfs', user.sfsLoanId]);
  }
  if (isStePricingGroup) {
    return router.createUrlTree(['/rehash-sfs']);
  }
  return 'dashboard';
}

/**
 * Default home redirect: SFS locked-loan users go to their loan; everyone else to dashboard.
 * Used as Angular `redirectTo` function (valid route config) and by {@link DefaultHomeRedirectGuard}.
 */
export function defaultHomeRedirectTo(): string | UrlTree {
  const authService = inject(AuthService);
  const router = inject(Router);
  const dealerState = inject(DealerStateService);
  return resolveDefaultHomeRedirect(authService.getUser(), router, dealerState.isSfsPricingGroup());
}

@Injectable({
  providedIn: 'root'
})
export class DefaultHomeRedirectGuard {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(): UrlTree {
    const result = resolveDefaultHomeRedirect(this.authService.getUser(), this.router);
    if (typeof result === 'string') {
      return this.router.createUrlTree([result]);
    }
    return result;
  }
}
